// src\hooks\useGenerateRoutes.ts
import React, { useCallback, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { agentRoutes } from "@/routes/agentRoutes";
import { authRoutes } from "@/routes/authRoutes";
import { customerRoutes } from "@/routes/customerRoutes";
import { publicRoutes } from "@/routes/publicRoutes";
import { superAdminRoutes } from "@/routes/superAdminRoutes";
import { superVisorRoutes } from "@/routes/superVisorRoutes";
import { useRolePermissions } from "@/hooks/useRolePermissions";
import { MemberLayout } from "@/layouts";
import { Agent, SuperVisor } from "@/types";
import { USER_TYPES } from "@/utils/constants";

const SUPPORT_MANAGER_GROUP_MENU_IDS = [58, 59, 61, 62];

export const useGenerateRoutes = () => {
  const {
    user,
    isGroupMember,
    isGroupUser,
    isMarketingManager,
    isSalesManager,
    isSupportManager,
  } = useRolePermissions();

  // The routing output depends only on the values below. updateUser() (fired by
  // each layout's profile fetch) replaces the user object reference on every
  // call, but does NOT change the user type or which menus are assigned. Keying
  // the memo at the bottom on a stable signature of these primitives keeps the
  // returned routes array referentially stable, so createBrowserRouter is not
  // rebuilt on every render. Rebuilding it remounted the whole tree, which
  // re-ran the profile + notification-count fetches in an infinite loop.
  const userType = user?.userType;
  const assignMenu = (user as Agent | SuperVisor | null)?.assignMenu;
  const isAuthenticated = Boolean(user);
  const menuSignature = (assignMenu ?? [])
    .map((item: any) => item?.menu?.id)
    .sort((a: number, b: number) => a - b)
    .join(",");
  const allowedMenuIds = useMemo(() => {
    const assignedMenuIds = menuSignature.split(",").filter(Boolean).map(Number);
    const combinedReportMenuIds = assignedMenuIds.some((id) => id === 61 || id === 62)
      ? [61, 62]
      : [];
    return [
      ...new Set([
        ...assignedMenuIds,
        ...combinedReportMenuIds,
        ...(isSupportManager ? SUPPORT_MANAGER_GROUP_MENU_IDS : []),
      ]),
    ];
  }, [isSupportManager, menuSignature]);

  const filterRoutesByMenu = useCallback((routes: any[]) => {
    if (allowedMenuIds.length === 0) return routes;

    // console.log(routes, "routes");

    const menuMap: { [key: string]: number } = {
      'dashboard': 1,
      'tickets': 2,
      'tickets/:id': 2,
      'ticket-categories': 47,
      'ticket-sub-categories': 47,
      'ticket-sub-sub-categories': 47,
      '/tickets/add': 2,
      'customer-query': 4,
      'scripts': 5,
      '/crm/customers': 6,
      '/crm/customers/add': 6,
      '/crm/customers/edit/:id': 6,
      '/crm/customers/details/:id': 6,
      '/crm/sales': 7,
      '/crm/sales/add-deal': 7,
      '/crm/sales/edit-deal': 7,
      '/crm/performance-report': 7,
      '/crm/leads': 10,
      '/crm/leads/add': 10,
      '/crm/leads/edit': 10,
      'attendance': 11,
      '/crm/sales/conversation': 10,
      '/crm/sales-query': 12,
      '/work-schedule': 13,
      '/shift-swaps': 14,
      '/leave-requests': 14,
      '/email/templates': 17,
      '/email/template/create': 17,
      '/email/template/update': 17,
      '/prospects': 18,
      '/prospect/add': 18,
      '/prospect/edit': 18,
      '/segments': 19,
      '/segment/add': 19,
      '/segment/update': 19,
      '/campaigns': 20,
      '/campaign/create': 20,
      '/campaign/update': 20,
      '/facebook-post-threads': 49,
      '/facebook-post-threads/:threadId': 49,
      '/facebook-conversations': 49,
      '/facebook-conversations/:conversationId': 49,
      // '/facebook-templates': 49,
      // '/facebook-templates/edit': 49,
      '/instagram-post-threads': 52,
      '/instagram-post-threads/:threadId': 52,
      '/instagram-conversations': 52,
      '/instagram-conversations/:conversationId': 52,
      // '/instagram-templates': 52,
      // '/instagram-templates/edit': 52,
      // WhatsApp inbox shares the WhatsApp menu permission (id 37)
      '/whatsapp-conversations': 37,
      '/whatsapp-conversations/:conversationId': 37,
      // 'settings/whatsapp/template/edit': 37,
      'settings/whatsapp': 37,
    };

    return routes.map(route => {
      if (route.path === "/" && route.children) {
        // Filter accessible routes
        const accessibleRoutes = route.children.filter((child: any) => {
          const path = child.path;
          if (path === "") return false;

          if (path === "profile") return true;
          if (path === "notifications") return true;


          const requiredMenuId = menuMap[path];
          return requiredMenuId && allowedMenuIds.includes(requiredMenuId);
        });

        // console.log("Accessible routes:", accessibleRoutes.map((c: any) => c.path));

        if (accessibleRoutes.length > 0) {
          const firstAccessibleRoute = accessibleRoutes[0].path;

          // Create the redirect route object without JSX
          const redirectRoute = {
            path: "",
            element: React.createElement(Navigate, { to: firstAccessibleRoute, replace: true })
          };

          const newChildren = [redirectRoute, ...accessibleRoutes,];

          return {
            ...route,
            children: newChildren
          };
        } else {
          return null;
        }
      }
      return route;
    }).filter(Boolean);
  }, [allowedMenuIds]);

  // console.log(assignMenu, "assignMenu");

  const filterRoutesByMenuForSupervisor = useCallback((routes: any[]) => {
    const shouldFilterByMenu = allowedMenuIds.length > 0;
    const groupTicketQueueMenuId = isGroupUser ? 60 : 58;

    const menuMap: { [key: string]: number } = {
      // id: 1 - Dashboard
      'dashboard': isSalesManager ? 28 : isMarketingManager ? 50 : 1,

      //id: 21 - subscription
      'subscription': 21,

      // id: 2 - Tickets
      'tickets': 2,
      'tickets/:id': 2,
      'customer-query': 4,
      'ticket-categories': 47,
      'ticket-sub-categories': 47,
      'ticket-sub-sub-categories': 47,

      // id: 22 - Agents
      'agents': 22,
      'agents/add': 22,
      'agents/:id': 22,

      // ids: 58-63 - Groups
      'groups': 58,
      'groups/details/:groupId': 58,
      'groups/members': 59,
      // The group ticket queue is one component with two audiences. Members and
      // group managers own it as their own menu (60); System Admins reach the
      // same screen from a Groups list row action and are gated by Groups (58),
      // because 60 is never assigned to a System Admin account.
      'groups/tickets': groupTicketQueueMenuId,
      'groups/tickets/:id': groupTicketQueueMenuId,
      // A plain Group Member's claimed tickets. Group Managers and System Admins
      // continue to work from the shared queue instead.
      'group-ticket': 63,
      'group-ticket/:id': 63,
      'ticket-volume-report': 61,
      'time-in-group-report': 62,

      // id: 23 - Agent Inquiry
      'agents-enquery': 23,

      // id: 24 - Knowledge Base
      '/knowledge-base': 24,
      '/knowledge-base/add': 24,
      '/knowledge-base/update': 24,

      // id: 25 - Reports Agent Call Activity
      'agents-report': 25,

      // id: 26 - Reports Agent Ticket Handling
      'agents-ticket-report': 26,
      'agents-ticket-report-details/:id': 26,

      // id: 27 - Reports Agent Attendance
      'agents-attendance-report': 27,

      // id: 28 - Reports Sales
      'sales-report': 28,

      // id: 48 - Reports CSAT
      'csat-report': 48,

      // id: 1 - Reports Ticket &  Analytics (shares the Dashboard menu id;
      // no dedicated backend menu id exists for this report yet)
      'ticket-call-analytics': 1,
      'agent-analytics-report': 1,


      // id: 6 - CRM Customer
      '/crm/customers': 6,
      '/crm/customers/add': 6,
      '/crm/customers/edit/:id': 6,
      '/crm/customers/details/:id': 6,
      '/crm/custom-fields': 6,
      '/crm/custom-fields/add': 6,
      '/crm/custom-fields/edit/:fieldId': 6,
      '/crm/custom-fields/view/:fieldId': 6,

      // id: 16 - CRM Request
      '/crm/requests': 16,


      // id: 28 - Sales Pipeline Dashboard
      '/sales-dashboard': 28,

      // id: 7 - CRM Sales
      '/crm/sales': 7,
      '/crm/sales/add-deal': 7,
      '/crm/sales/edit-deal': 7,
      '/crm/sales/conversation': 7,


      // id: 10 - CRM Leads
      '/crm/leads': 10,
      '/crm/leads/add': 10,
      '/crm/leads/edit': 10,

      // id: 29 - Sales Pipeline Categories
      '/crm/category': 29,
      '/crm/category/add': 29,
      '/crm/category/edit': 29,

      // id: 30 - Sales Pipeline Sub Categories
      '/crm/sub-category': 30,
      '/crm/sub-category/add': 30,
      '/crm/sub-category/edit': 30,

      // id: 31 - Sales Pipeline Regions
      '/crm/region': 31,
      '/crm/region/add': 31,
      '/crm/region/edit': 31,

      // id: 12 - Sales Query
      '/crm/sales-query': 12,



      // id: 17 - Marketing Email Templates
      '/email/templates': 17,
      '/email/template/create': 17,
      '/email/template/update': 17,

      // id: 18 - Marketing Prospects
      '/prospects': 18,
      '/prospect/add': 18,
      '/prospect/edit': 18,

      // id: 19 - Marketing Segments
      '/segments': 19,
      '/segment/add': 19,
      '/segment/update': 19,

      // id: 20 - Marketing Campaigns
      '/campaigns': 20,
      '/campaign/create': 20,
      '/campaign/update': 20,

      // id: 42 - workforce 
      'workforce-overview': 42,
      'all-leave-requests': 42,
      'all-swap-requests': 42,
      'shift-management': 43,
      'rules-settings': 44,
      'shift-calendar': 45,
      'staff-list': 46,
      'staff-details': 46,

      // id: 32 - Settings Chatbot
      'settings/chatbot-questions': 32,

      // id: 33 - Settings Ticket
      'settings/ticketing-system': 33,

      // id: 34 - Settings Global
      'settings/global': 34,

      // id: 35 - Settings Custom Scripts
      'settings/custom-scripts': 35,

      // id: 36 - Settings SLA
      'settings/sla': 36,
      'settings/sla-dashboard': 36,

      // id: 37 - Settings WhatsApp (also gates the WhatsApp conversations inbox)
      'settings/whatsapp': 37,
      'settings/connect-whatsapp': 37,
      'settings/whatsapp/template/edit': 37,
      '/whatsapp-conversations': 37,
      '/whatsapp-conversations/:conversationId': 37,

      // id: 39 - Auxiliary API
      'api-docs': 39,

      // id: 40 - Feedback
      'feedback': 40,
      //id: 41 - Managers
      'managers': 41,
      'manager/add': 41,
      'manager/update': 41,
      //id: 49 - Facebook Post Threads
      '/facebook-post-threads': 49,
      '/facebook-post-threads/:threadId': 49,
      // Facebook DM conversations inbox (shares the Facebook menu permission, id 49)
      '/facebook-conversations': 49,
      '/facebook-conversations/:conversationId': 49,
      '/facebook-templates': 49,
      '/facebook-templates/edit': 49,
      //id: 50 - Marketing Report
      'marketing-report': 50,
      //id: 51 - Settings Facebook
      // 'settings/facebook': 51,
      // id: 52 - Instagram Post Threads
      '/instagram-post-threads': 52,
      '/instagram-post-threads/:threadId': 52,
      // Instagram DM conversations inbox (shares the Instagram menu permission, id 52)
      '/instagram-conversations': 52,
      '/instagram-conversations/:conversationId': 52,
      '/instagram-templates': 52,
      '/instagram-templates/edit': 52,

      // id: 57 - Retention Management
      '/retention/policies': 57,
      '/retention/policies/:id': 57,
      '/retention/archive-records': 57,
      '/retention/archive-records/:id': 57,
      '/retention/legal-holds': 57,
      '/retention/audit-trail': 57,
    };

    return routes.map(route => {
      if (route.path === "/" && route.children) {
        // Filter accessible routes
        const accessibleRoutes = route.children.filter((child: any) => {
          const path = child.path;
          if (path === "") return false;

          if (path === "profile") return true;
          if (path === "notifications") return true;
          if ((path === 'group-ticket' || path === 'group-ticket/:id') && !isGroupMember) {
            return false;
          }
          if (
            (path === 'ticket-volume-report' || path === 'time-in-group-report')
            && isGroupMember
          ) {
            return false;
          }
          if (!shouldFilterByMenu) return true;

          const requiredMenuId = menuMap[path];
          // Path not in menuMap → utility/sub-page route, always allow
          if (requiredMenuId === undefined) return true;
          return allowedMenuIds.includes(requiredMenuId);
        });

        if (accessibleRoutes.length > 0) {
          const firstMenuRoute = accessibleRoutes.find((c: any) => menuMap[c.path] !== undefined);
          const firstAccessibleRoute = (firstMenuRoute ?? accessibleRoutes[0]).path;

          const redirectRoute = {
            path: "",
            element: React.createElement(Navigate, { to: firstAccessibleRoute, replace: true })
          };

          const newChildren = [redirectRoute, ...accessibleRoutes];

          return {
            ...route,
            children: newChildren
          };
        } else {
          return null;
        }
      }
      return route;
    }).filter(Boolean);
  }, [allowedMenuIds, isGroupMember, isGroupUser, isMarketingManager, isSalesManager]);

  const setGroupMemberLandingRoute = useCallback((routes: any[]) => {
    return routes.map((route) => {
      if (route.path !== '/' || !route.children) return route;

      const accessibleRoutes = route.children.filter(
        (child: any) => child.path !== '',
      );
      const preferredPaths = isGroupMember
        ? ['group-ticket', 'groups/tickets']
        : ['groups/tickets', 'group-ticket'];
      const preferredRoute = preferredPaths
        .map((path) => accessibleRoutes.find((child: any) => child.path === path))
        .find(Boolean) ?? accessibleRoutes[0];
      const redirectRoute = {
        path: '',
        element: React.createElement(Navigate, {
          to: preferredRoute?.path ? `/${preferredRoute.path}` : '/profile',
          replace: true,
        }),
      };

      return {
        ...route,
        element: React.createElement(MemberLayout),
        children: [redirectRoute, ...accessibleRoutes],
      };
    });
  }, [isGroupMember]);

  // Intentionally keyed on stable primitives (not the per-render user/assignMenu
  // refs) to avoid rebuilds.
  return useMemo(() => {
    // Not logged in → only public + auth routes
    if (!isAuthenticated) return [...publicRoutes, ...authRoutes];

    if (userType === USER_TYPES.SUPER_ADMIN) return [...publicRoutes, ...superAdminRoutes];
    if (userType === USER_TYPES.SUPERVISOR) {
      const filteredRoutes = filterRoutesByMenuForSupervisor(superVisorRoutes);
      return [...publicRoutes, ...filteredRoutes];
    }
    if (userType === USER_TYPES.AGENT) {
      const filteredRoutes = filterRoutesByMenu(agentRoutes);
      return [...publicRoutes, ...filteredRoutes];
    }
    if (userType === USER_TYPES.CUSTOMER) return [...publicRoutes, ...customerRoutes];
    if (userType === USER_TYPES.MEMBER) {
      const assignedRoutes = filterRoutesByMenuForSupervisor(superVisorRoutes);
      return [...publicRoutes, ...setGroupMemberLandingRoute(assignedRoutes)];
    }

    return publicRoutes;
  }, [
    filterRoutesByMenu,
    filterRoutesByMenuForSupervisor,
    setGroupMemberLandingRoute,
    isAuthenticated,
    userType,
  ]);
};
